import type { GnomeAssetPriority } from "./gnomeSpineAssetTransport.ts";

interface PreparationTask {
    tags: readonly string[];
    priority: GnomeAssetPriority;
    work(): void | Promise<void>;
    resolve(): void;
    reject(error: unknown): void;
}

/** One CPU/decode stage at a time. A foreground demand bypasses idle scheduling. */
export function createGnomePreparationQueue(
    idle: (run: () => void) => () => void = (run) => {
        if (typeof requestIdleCallback === "function") {
            const id = requestIdleCallback(run);
            return () => cancelIdleCallback(id);
        }
        const id = setTimeout(run, 50);
        return () => clearTimeout(id);
    },
) {
    const queue: PreparationTask[] = [];
    const foreground = new Map<string, number>();
    let enabled = false;
    let active = false;
    let cancelIdle: (() => void) | null = null;
    let scheduled = false;
    const urgent = (task: PreparationTask) =>
        task.priority === "foreground" ||
        task.tags.some((tag) => foreground.has(tag));

    function run(task: PreparationTask): void {
        queue.splice(queue.indexOf(task), 1);
        active = true;
        void Promise.resolve()
            .then(task.work)
            .then(task.resolve, task.reject)
            .finally(() => {
                active = false;
                schedule();
            });
    }

    function schedule(): void {
        cancelIdle?.();
        cancelIdle = null;
        if (active || scheduled) return;
        scheduled = true;
        queueMicrotask(() => {
            scheduled = false;
            if (active) return;
            const demanded = queue.find(urgent);
            if (demanded) {
                run(demanded);
                return;
            }
            if (!enabled || !queue.length) return;
            cancelIdle = idle(() => {
                cancelIdle = null;
                const task =
                    queue.find(urgent) ?? (enabled ? queue[0] : undefined);
                if (task) run(task);
            });
        });
    }

    return {
        schedule(
            tags: readonly string[],
            priority: GnomeAssetPriority,
            work: () => void | Promise<void>,
        ): Promise<void> {
            const ready = new Promise<void>((resolve, reject) =>
                queue.push({ tags, priority, work, resolve, reject }),
            );
            schedule();
            return ready;
        },
        demand(tags: readonly string[]): () => void {
            for (const tag of tags)
                foreground.set(tag, (foreground.get(tag) ?? 0) + 1);
            schedule();
            let released = false;
            return () => {
                if (released) return;
                released = true;
                for (const tag of tags) {
                    const count = (foreground.get(tag) ?? 1) - 1;
                    if (count) foreground.set(tag, count);
                    else foreground.delete(tag);
                }
                schedule();
            };
        },
        setEnabled(value: boolean) {
            enabled = value;
            schedule();
        },
    };
}

export const gnomePreparationQueue = createGnomePreparationQueue();
